import { AsyncPipe } from '@angular/common';
import type { Signal } from '@angular/core';
import {
	Component,
	computed,
	CUSTOM_ELEMENTS_SCHEMA,
	effect,
	inject,
	input,
	output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import dayjs from 'dayjs';
import type { Observable } from 'rxjs';
import { Subject, switchMap } from 'rxjs';

import type { AvailabilityInputDropdownItem } from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-input-dropdown/availability-input-dropdown.interface';
import {
	AvailabilityWidgetComponent as UIAvailabilityWidget,
	occupancyRestrictions as defaultOccupancyRestrictions,
} from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-widget.component';
import type { WidgetDataActionInterface } from '@valk-nx/compositions/ui-availability-widget/src/lib/availability-widget.component.interface';
import type { Language } from '@valk-nx/core/lib/core';
import { HOTEL_SLUG, LANGUAGE } from '@valk-nx/core/lib/core';
import type {
	AccommodationTypeInterface,
	ContentHotelInterface,
	HotelOccupancyRestrictionsInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import {
	clampToMaxBookingDate,
	getMaxBookingDate,
} from '@valk-nx/helpers/lib/dates/advance-booking.helper';
import type { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import { TagManagerFacade } from '@valk-nx/storyblok-store/src/lib/store/tag-manager/tag-manager.facade';

@Component({
	selector: 'sb-availability-widget',
	templateUrl: './availability-widget.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [AsyncPipe, UIAvailabilityWidget],
})
export class AvailabilityWidgetComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly tagManagerFacade = inject(TagManagerFacade);
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	readonly configHotelSlug = inject(HOTEL_SLUG);
	readonly hotelStore = inject(HotelStore);
	readonly bookingtoolUrl = inject(BOOKINGTOOL);

	hotelGuid = input<string | undefined>(undefined);
	hotelSlug = input<string>(this.configHotelSlug);
	roomGuid = input<string | undefined>(undefined);
	roomSlug = input<string | undefined>(undefined);
	dealGuid = input<string | undefined>(undefined);
	dealSlug = input<string | undefined>(undefined);
	packageGuid = input<string | undefined>(undefined);
	packageSlug = input<string | undefined>(undefined);
	forceHideRangePrices = input<boolean>(false);

	accommodationTypes = input<AccommodationTypeInterface[] | undefined>(
		undefined,
	);
	initialOccupancy = input<OccupancyInterface[] | undefined>(undefined);
	defaultAdults = input<number>(2);
	occupancyRestrictions = input<OccupancyRestrictionsInterface | undefined>(
		undefined,
	);

	initialArrivalDate = input<Date | undefined>(undefined);
	minDate = input<Date>(dayjs().subtract(1, 'day').toDate());
	maxDate = input<Date | undefined>(undefined);

	hotels = input<AvailabilityInputDropdownItem[]>([]);
	stepperConfig = input<{
		useStepper: boolean;
		min: number;
		max: number | undefined;
	}>({
		useStepper: false,
		min: 1,
		max: undefined,
	});

	currency = input<string>('EUR');
	showOccupancy = input<boolean>(true);
	showDates = input<boolean>(true);
	showPrices = input<boolean>(true);
	showPackages = input<boolean>(false);
	showAccommodationTypes = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);
	openAvailabilityInNewTab = input<boolean>(false);
	language = input<string>(this.rootLanguage);
	includeVhiHotels = input<boolean>(false);

	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	packageSelected = output<number>();

	hotelsWithOccupancyRestrictions: Signal<
		HotelOccupancyRestrictionsInterface[] | undefined
	>;
	showHotels = computed(() => !this.hotelSlug());
	effectiveMaxDate = computed(() => {
		if (this.includeVhiHotels()) {
			return getMaxBookingDate(undefined, true);
		}

		return clampToMaxBookingDate(
			this.maxDate(),
			this.hotel?.()?.maxAdvanceBookingMonths,
		);
	});

	hotel$: Observable<ContentHotelInterface> | undefined;
	hotel: Signal<ContentHotelInterface | undefined> | undefined;
	defaultOccupancyRestrictions = defaultOccupancyRestrictions;

	private readonly hotelParams$ = new Subject<{
		slug: string;
		lang: string;
	}>();
	private readonly occupancyRestrictionsParams$ = new Subject<string>();

	constructor() {
		super();

		this.hotel$ = this.hotelParams$.pipe(
			switchMap(({ lang }) => this.contentService.getHotelInfo(lang)),
		);
		this.hotel = toSignal(this.hotel$);

		this.hotelsWithOccupancyRestrictions = toSignal(
			this.occupancyRestrictionsParams$.pipe(
				switchMap((lang) =>
					this.contentService.getHotelsWithOccupancyRestrictions(
						lang,
						this.includeVhiHotels(),
					),
				),
			),
		);

		effect(() => {
			const slug = this.hotelSlug() ?? this.hotelStore.slug();
			const lang = this.language();

			if (slug) {
				this.hotelParams$.next({ slug, lang });
			} else {
				this.occupancyRestrictionsParams$.next(lang);
			}
		});
	}

	widgetCTA(eventData: {
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}) {
		const hotelSlug = this.hotelSlug();
		const pmsCode = this.hotelStore.pmsCode();
		const roomSlug = this.roomSlug();
		const dealSlug = this.dealSlug();
		const packageSlug = this.packageSlug();

		this.tagManagerFacade.availabilityWidget({
			...eventData,
			data: {
				...eventData.data,
				hotelSlug,
				...(pmsCode !== undefined && { pmsCode }),
				...(roomSlug !== undefined && { roomSlug }),
				...(dealSlug !== undefined && { dealSlug }),
				...(packageSlug !== undefined && { packageSlug }),
			},
		});
	}
}
